// services/auth.js
import { setUserSession, validateLoginResponse } from '../utils/auth';

// Obtener la URL base de la API desde las variables de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Realiza el login del usuario y guarda la sesión
 * @param {string} username - Nombre de usuario
 * @param {string} password - Contraseña
 * @returns {Promise<Object>} Datos del login con validación
 */
export const login = async (username, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/User/CheckLogin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },
      body: JSON.stringify({
        username,
        password,
      }),
    });

    // validar respuesta
    if (!response.ok) {
      let errorMessage = 'Error en el login';
      let errorType = 'authentication'; // Por defecto es error de autenticación

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
        
        // Si el backend indica específicamente que es error de credenciales
        if (response.status === 401 || response.status === 403) {
          errorType = 'authentication';
        }
      } catch {
        // si no viene json, dejamos mensaje genérico
        // Si no se puede parsear, podría ser error de red/servidor
        if (response.status >= 500) {
          errorType = 'server';
        }
      }

      const error = new Error(errorMessage);
      error.type = errorType;
      error.status = response.status;
      throw error;
    }

    const data = await response.json();

    // Validar la respuesta del login usando la nueva utilidad
    const validation = validateLoginResponse(data);

    // Solo guardamos la sesión si el usuario puede proceder (activo)
    // Esto incluye casos donde debe cambiar contraseña
    if (validation.canProceed) {
      // ✅ Procesar nombre y apellido
      let firstName = data.name || data.username;
      // El API devuelve lastName con mayúscula
      let lastName = data.lastName || data.lastname || data.surname || '';
      
      // Si no viene lastname pero name tiene espacios, separarlo
      if (!lastName && data.name && data.name.includes(' ')) {
        const nameParts = data.name.trim().split(' ');
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
      }
      
      setUserSession({
        id: data.id,
        username: data.username,
        name: firstName,
        lastname: lastName,
        email: data.email,
        group_ID: data.groupID || null,
        groupname: data.groupname || null,
        sessionId: data.sessionID || data.sessionId || null,
        modules: data.modules || [],
        status: data.userStatus ?? 1,
        userResetPassword: data.passwordResetFlag ?? 0,
        ce_ID: data.ceID || null,
        creationDate: data.creationDate || null,
        lastLoginDate: data.lastLoginDate || null
      });
    }

    // Retornar data con la validación agregada
    return {
      ...data,
      validation,
    };

  } catch (error) {
    console.error('Error auth:', error);

    // Preservar el tipo de error si existe
    const newError = new Error(error.message || 'No se pudo conectar con el servidor');
    newError.type = error.type || 'connection';
    newError.status = error.status;
    throw newError;
  }
};
